import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  DatePicker,
  Flashbar,
  Header,
  Input,
  Link,
  Pagination,
  Popover,
  SpaceBetween,
  Table,
} from "@amzn/awsui-components-react";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Select from "@amzn/awsui-components-core/select";
import Checkbox from "@amzn/awsui-components-core/checkbox";
import RadioButton from "@amzn/awsui-components-core/radio-button";
import { OptionDefinition } from "@amzn/awsui-components-core/internal/components/option/interfaces";
import * as tokens from "@amzn/awsui-design-tokens-core";
import { useRecentTransactions } from "../hooks/useRecentTransactions";
import {
  COLUMN_DEFINITIONS,
  DEFAULT_OPTION,
  getFeatureGroup,
  getModifiedOptions,
  GLS,
  GROUPED_FEATURES,
  GROUPED_MARKETPLACES,
  GROUPED_USECASES,
  PAYMENT_METHOD_CLASS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  RECENT_TRXN_EMPTY_STATE,
  STAGES,
  TABLE_PREFERENCES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import ViewLogs from "./common/ViewLogs";
import { publisherLogGroups } from "../helpers/logUtils";
import { EmptyState } from "./common/EmptyState";
import { Transaction, Stage } from "../lusca-search-api/generated-src";
import { SearchFilters } from "../types/common";
import { useFolderUI } from "../hooks/useFolderUI";
import useFolders from "../hooks/useFolders";
import { FolderModal } from "./folders/FolderModal";
import { getGlMapping } from "../hooks/useSummaryData";
import { SavedSearchesManager } from "./SavedSearchesManager";
import { SavedSearch } from "../hooks/useSavedSearches";
import {
  parseFiltersFromUrl,
  serializeFilterState,
} from "../helpers/recentTransactionsUtils";

interface RecentTransactionsProps {
  isRaven?: boolean;
  selectedOrders?: Transaction[];
  setSelectedOrders?: (orders: Transaction[]) => void;
}

export default function RecentTransactions(props: RecentTransactionsProps) {
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transaction[]
  >([]);
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI();

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    handleSearch,
    isLoading,
    selectedFilters,
    setSelectedFilters,
    clearFilter,
    executedQuery,
    logGroups,
    error: searchError,
  } = useRecentTransactions();

  const modifiedMarketplaces = useMemo(
    () =>
      getModifiedOptions(GROUPED_MARKETPLACES, selectedFilters.marketplaces),
    [selectedFilters.marketplaces]
  );

  const modifiedUsecases = useMemo(
    () => getModifiedOptions(GROUPED_USECASES, selectedFilters.usecases),
    [selectedFilters.usecases]
  );

  const modifiedFeatures = useMemo(() => {
    const features = getModifiedOptions(
      GROUPED_FEATURES,
      selectedFilters.features
    );

    // Add reset button as first item if there are selections
    if (selectedFilters.features.length > 0) {
      return [{ label: "__RESET__", value: "__RESET__" }, ...features];
    }

    return features;
  }, [selectedFilters.features]);

  // Custom renderer for Features multiselect
  // Show unselectable group headers and then radio buttons or checkboxes
  const renderFeatureOption = ({
    item,
  }: {
    item: {
      type: string;
      option?: { value?: string; label?: string };
      parent?: unknown;
      disabled?: boolean;
    };
  }) => {
    // Handle reset button
    if (item.option?.value === "__RESET__") {
      return (
        <div
          style={{
            padding: tokens.spaceScaledS,
            borderBottom: `1px solid ${tokens.colorBorderDividerDefault}`,
            textAlign: "center",
            backgroundColor: tokens.colorBackgroundContainerContent,
          }}
        >
          <Button
            variant="inline-link"
            iconName="close"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFilters({
                ...selectedFilters,
                features: [],
              });
            }}
          >
            Reset all selections
          </Button>
        </div>
      );
    }

    // Display group name - clicking clears the group
    if (item.type === "group") {
      return (
        <div
          style={{
            padding: `${tokens.spaceScaledXxs} ${tokens.spaceScaledS}`,
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            const groupLabel = item.option?.label;
            if (groupLabel) {
              const filtered = selectedFilters.features.filter(
                (opt) => getFeatureGroup(opt.value!)?.label !== groupLabel
              );
              setSelectedFilters({ ...selectedFilters, features: filtered });
            }
          }}
        >
          {item.option?.label}
        </div>
      );
    }

    if (item.type !== "item" || !item.option) return null;

    const group = getFeatureGroup(item.option.value || "");
    const isSelected = selectedFilters.features.some(
      (f) => f.value === item.option?.value
    );

    return (
      <div
        style={{
          padding: `${tokens.spaceScaledXxs} ${tokens.spaceScaledS}`,
          paddingInlineStart: item.parent
            ? tokens.spaceScaledXxl
            : tokens.spaceScaledS,
        }}
      >
        {group?.multiSelect ? (
          <Checkbox checked={isSelected}>{item.option.label}</Checkbox>
        ) : (
          <RadioButton
            checked={isSelected}
            value=""
            name={group?.label || "feature"}
          >
            {item.option.label}
          </RadioButton>
        )}
      </div>
    );
  };

  const handleMultiselectChange =
    <K extends keyof SearchFilters>(filter: K) =>
    ({
      detail,
    }: {
      detail: { selectedOptions: readonly OptionDefinition[] };
    }) => {
      let newSelection = [...detail.selectedOptions] as OptionDefinition[];
      const currentSelection = selectedFilters[filter] as OptionDefinition[];
      const hadAll = currentSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );
      const hasAll = newSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );

      if (hasAll && !hadAll) {
        newSelection = [DEFAULT_OPTION];
      } else if (hadAll) {
        newSelection = [];
      }
      setSelectedFilters({
        ...selectedFilters,
        [filter]: newSelection,
      });
    };

  const handleFeaturesChange = ({
    detail,
  }: {
    detail: { selectedOptions: readonly OptionDefinition[] };
  }) => {
    const newSelection = (
      [...detail.selectedOptions] as OptionDefinition[]
    ).filter((opt) => opt.value && opt.value !== "__RESET__");
    const prevSelection = selectedFilters.features;

    const addedItems = newSelection.filter(
      (opt) => !prevSelection.some((p) => p.value === opt.value)
    );

    // No new items selected, so user deselected items from the group
    if (addedItems.length === 0) {
      setSelectedFilters({ ...selectedFilters, features: newSelection });
      return;
    }

    const group = getFeatureGroup(addedItems[0].value!);

    // Radio buttons - only keep the latest selection
    if (group && !group.multiSelect) {
      const filtered = newSelection.filter(
        (opt) =>
          getFeatureGroup(opt.value!)?.label !== group.label ||
          opt.value === addedItems[0].value
      );
      setSelectedFilters({ ...selectedFilters, features: filtered });
      return;
    }

    setSelectedFilters({ ...selectedFilters, features: newSelection });
  };

  const handleAddToFolder = () => {
    if (selectedTransactions.length === 0) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && selectedTransactions.length > 0) {
      const result = addTransactions(
        selectedFolderId,
        selectedTransactions.map((transaction) => ({
          date: new Date().toISOString(),
          id: transaction.id || "",
          stage: selectedFilters.stage.value as Stage,
          status: "unknown",
          country: transaction.country,
          gl: transaction.gl ? getGlMapping(transaction.gl) : "",
          usecase: transaction.usecase || "",
          paymentMethod: transaction.paymentMethod || "",
          labels: [],
          consumptionTypes: [],
        }))
      );

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.success) {
        setSelectedTransactions([]);
        handleModalDismiss();
      }
    }
  };

  const tableFooter = useMemo(() => {
    if (isLoading || items.length >= 15 || selectedFilters.deep) {
      return null;
    }

    return (
      <Box textAlign="center">
        {items.length === 0
          ? "No results found. "
          : "Limited results found in default search window. "}
        <Link
          onFollow={() => {
            setSelectedFilters((f) => ({ ...f, deep: true }));
            handleSearch({ deep: true });
          }}
        >
          Search deeper?
        </Link>{" "}
      </Box>
    );
  }, [items.length, selectedFilters.deep, isLoading]);

  const loadingText = useMemo(() => {
    if (!isLoading) return "";
    return selectedFilters.deep
      ? "Performing deep search (this may take longer)..."
      : "Searching for transactions";
  }, [isLoading, selectedFilters.deep]);

  const getCurrentFilters = () =>
    serializeFilterState(selectedFilters).toString();

  const handleLoadSearch = (search: SavedSearch) => {
    const filters = parseFiltersFromUrl(new URLSearchParams(search.filters));
    setSelectedFilters(filters);
    handleSearch(filters);
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Table
        {...collectionProps}
        {...TABLE_PREFERENCES}
        resizableColumns
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loading={isLoading}
        loadingText={loadingText}
        selectionType="multi"
        onSelectionChange={({ detail }) => {
          if (props.isRaven && props.setSelectedOrders) {
            props.setSelectedOrders(detail.selectedItems);
          } else if (!props.isRaven) {
            setSelectedTransactions(detail.selectedItems);
          }
        }}
        selectedItems={
          props.isRaven ? props.selectedOrders : selectedTransactions
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ViewLogs
                  stage={selectedFilters.stage.value as Stage}
                  logGroups={
                    logGroups && logGroups.length > 0
                      ? logGroups
                      : publisherLogGroups(selectedFilters.stage.value as Stage)
                  }
                  logQuery={executedQuery!}
                  minimal
                />
                {!props.isRaven && (
                  <Button
                    onClick={handleAddToFolder}
                    disabled={selectedTransactions.length === 0}
                  >
                    Add to Folder
                  </Button>
                )}
                {!props.isRaven && (
                  <SavedSearchesManager
                    getFilters={getCurrentFilters}
                    stage={selectedFilters.stage.value as Stage}
                    onLoadSearch={handleLoadSearch}
                  />
                )}
              </SpaceBetween>
            }
          >
            Find Transactions{" "}
            <Popover
              dismissButton={false}
              fixedWidth={false}
              position="bottom"
              triggerType="custom"
              content="Searches look at the last 2 weeks of logs from your specified start time. To choose a specific start time range, or add custom filters, open in CloudWatch"
            >
              <Button iconName="status-info" variant="inline-icon" />
            </Popover>
          </Header>
        }
        filter={
          <div className="input-container">
            <div className="select-filter" style={{ maxInlineSize: "100px" }}>
              <Select
                data-testid="stage-filter"
                inlineLabelText="Stage"
                options={STAGES}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.stage}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    stage: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="GL"
                data-testid="gl-filter"
                options={GLS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.gl}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    gl: detail.selectedOption,
                  })
                }
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find GL"
                empty="No matching GLs"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="Payment Type"
                data-testid="payment-method-filter"
                options={PAYMENT_METHOD_OPTIONS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentMethod}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    paymentMethod: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "180px" }}>
              <Select
                inlineLabelText="Payment Method Class"
                data-testid="payment-method-class-filter"
                options={PAYMENT_METHOD_CLASS_OPTIONS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentMethodClass}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    paymentMethodClass: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Marketplaces"
                inlineTokens
                data-testid="marketplace-filter"
                options={modifiedMarketplaces}
                selectedOptions={selectedFilters.marketplaces}
                onChange={handleMultiselectChange("marketplaces")}
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find marketplaces"
                empty="No matching marketplaces"
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Usecases"
                data-testid="usecase-filter"
                inlineTokens
                options={modifiedUsecases}
                selectedOptions={selectedFilters.usecases}
                onChange={handleMultiselectChange("usecases")}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find usecases"
                empty="No matching usecases"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "180px" }}>
              <Input
                data-testid="asin-filter"
                value={selectedFilters.asin}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    asin: detail.value,
                  })
                }
                placeholder="ASIN"
                inputMode="search"
              />
            </div>
            <div className="select-filter" style={{ minInlineSize: "200px" }}>
              <Multiselect
                placeholder="Features"
                data-testid="features-filter"
                inlineTokens
                options={modifiedFeatures}
                selectedOptions={selectedFilters.features}
                onChange={handleFeaturesChange}
                renderOption={renderFeatureOption}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find Features"
                empty="No matching features"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="Time Range"
                data-testid="time-range-filter"
                options={TIME_RANGES}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.timeRange}
                onChange={({ detail }) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    timeRange: detail.selectedOption,
                    date:
                      detail.selectedOption.value !== "custom"
                        ? ""
                        : selectedFilters.date,
                  });
                }}
                expandToViewport
              />
            </div>
            {selectedFilters.timeRange.value === "custom" && (
              <div className="select-filter" style={{ maxInlineSize: "200px" }}>
                <DatePicker
                  onChange={({ detail }) =>
                    setSelectedFilters({
                      ...selectedFilters,
                      date: detail.value,
                    })
                  }
                  value={selectedFilters.date}
                  placeholder="Select a date"
                  previousMonthAriaLabel="Previous month"
                  nextMonthAriaLabel="Next month"
                  todayAriaLabel="Today"
                  expandToViewport
                  isDateEnabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return new Date(date) <= today;
                  }}
                />
              </div>
            )}

            <Button variant="primary" onClick={() => handleSearch()}>
              Search
            </Button>
            <Button onClick={clearFilter}>Clear filters</Button>
          </div>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <EmptyState
            error={searchError}
            filteringText={filterProps.filteringText}
            config={RECENT_TRXN_EMPTY_STATE}
            action={
              !selectedFilters.deep &&
              !filterProps.filteringText &&
              !searchError
                ? {
                    text: "Search deeper?",
                    onClick: () => handleSearch({ deep: true }),
                  }
                : undefined
            }
          />
        }
        footer={items.length > 0 && !isLoading ? tableFooter : null}
      />

      {!props.isRaven && (
        <FolderModal
          isModalVisible={isModalVisible}
          isFolderModalVisible={isFolderModalVisible}
          isShareModalVisible={false}
          selectedFolderId={selectedFolderId}
          folderName={folderName}
          error={error}
          folders={folders}
          sharingFolder={null}
          onModalDismiss={handleModalDismiss}
          onCreateFolder={handleCreateFolder}
          onFolderSelect={handleFolderSelect}
          onFolderModalDismiss={handleFolderModalDismiss}
          onShareModalDismiss={() => {}}
          onSaveFolder={() => handleSaveFolder(saveFolder)}
          setSelectedFolderId={setSelectedFolderId}
          setFolderName={setFolderName}
        />
      )}
    </>
  );
}
